import { useState } from "react";
import { toast } from "sonner";
import { format, subMonths } from "date-fns";
import { FileCsv, Play } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = subMonths(new Date(), i);
  return { value: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") };
});

const CAT_LABELS = { utilities: "Utilities", staff_wages: "Staff wages", maintenance: "Maintenance", supplies: "Supplies", other: "Other" };
const CHANNEL_LABELS = { walk_in: "Walk-in", phone: "Phone", website: "Website", easystay_app: "EasyStay App" };

export function DailyReportSection() {
  const [day, setDay] = useState(format(new Date(), "yyyy-MM-dd"));
  const [daily, setDaily] = useState(null);
  const { fmt } = useCurrency();

  const runDaily = async () => {
    try {
      const { data } = await api.get(`/accounting/reports/daily?day=${day}`);
      setDaily(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid="daily-report-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Daily summary</h2>
          <p className="text-xs text-muted-foreground">Revenue, expenses and net for a single day.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-44"><DatePicker testId="daily-report-date" value={day} onChange={setDay} /></div>
          <Button onClick={runDaily} data-testid="run-daily-report" className="transition-colors"><Play size={15} className="mr-1.5" /> Run</Button>
          {daily && (
            <Button variant="outline" data-testid="export-daily-csv" className="transition-colors"
              onClick={() => window.open(`${API}/accounting/reports/daily.csv?day=${day}`, "_blank")}>
              <FileCsv size={15} className="mr-1.5" /> Export CSV
            </Button>
          )}
        </div>
      </div>
      {daily && (
        <div className="mt-4" data-testid="daily-report-result">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {[
              ["Collected", fmt(daily.collected_total), "daily-collected"],
              ["Booked (new)", fmt(daily.booked_total), "daily-booked"],
              ["Arrivals", daily.arrivals, "daily-arrivals"],
              ["Departures", daily.departures, "daily-departures"],
              ["Expenses", fmt(daily.expenses_total), "daily-expenses"],
              ["Net", fmt(daily.net), "daily-net"],
            ].map(([label, value, testId]) => (
              <div key={testId} className="border border-border p-3" data-testid={testId}>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className={`font-heading mt-1 text-xl font-bold ${testId === "daily-net" ? (daily.net >= 0 ? "text-status-confirmed" : "text-destructive") : ""}`}>{value}</div>
              </div>
            ))}
          </div>
          {daily.expenses.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">
              Expenses: {daily.expenses.map((e) => `${CAT_LABELS[e.category]} ${fmt(e.amount)}`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MonthlyReportSection() {
  const [month, setMonth] = useState(MONTHS[0].value);
  const [pl, setPl] = useState(null);
  const { fmt } = useCurrency();

  const runMonthly = async () => {
    try {
      const { data } = await api.get(`/accounting/reports/monthly?month=${month}`);
      setPl(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid="monthly-report-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Monthly profit &amp; loss</h2>
          <p className="text-xs text-muted-foreground">Collected revenue vs operating expenses, with channel and category breakdowns.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-44" data-testid="monthly-report-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={runMonthly} data-testid="run-monthly-report" className="transition-colors"><Play size={15} className="mr-1.5" /> Run</Button>
          {pl && (
            <Button variant="outline" data-testid="export-monthly-csv" className="transition-colors"
              onClick={() => window.open(`${API}/accounting/reports/monthly.csv?month=${month}`, "_blank")}>
              <FileCsv size={15} className="mr-1.5" /> Export CSV
            </Button>
          )}
        </div>
      </div>
      {pl && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="monthly-report-result">
          <div className="border border-border p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Revenue (booked, by channel)</div>
            <div className="mt-3 space-y-2 text-sm">
              {Object.entries(pl.booked_by_channel).map(([ch, v]) => (
                <div key={ch} className="flex justify-between"><span>{CHANNEL_LABELS[ch] || ch}</span><span className="font-medium">{fmt(v)}</span></div>
              ))}
              <div className="flex justify-between border-t border-border pt-2 font-heading font-bold"><span>Total booked</span><span>{fmt(pl.booked_total)}</span></div>
              <div className="flex justify-between text-status-confirmed"><span>Collected ({pl.collected_count} payments)</span><span className="font-medium">{fmt(pl.collected_total)}</span></div>
            </div>
          </div>
          <div className="border border-border p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expenses (by category)</div>
            <div className="mt-3 space-y-2 text-sm">
              {Object.keys(pl.expenses_by_category).length === 0 && <div className="text-muted-foreground">No expenses this month.</div>}
              {Object.entries(pl.expenses_by_category).map(([cat, v]) => (
                <div key={cat} className="flex justify-between" data-testid={`pl-expense-${cat}`}><span>{CAT_LABELS[cat] || cat}</span><span className="font-medium">{fmt(v)}</span></div>
              ))}
              <div className="flex justify-between border-t border-border pt-2 font-heading font-bold"><span>Total expenses</span><span data-testid="pl-expenses-total">{fmt(pl.expenses_total)}</span></div>
            </div>
          </div>
          <div className={`flex flex-col justify-center border p-6 text-center ${pl.net >= 0 ? "border-status-confirmed/40 bg-status-confirmed/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="pl-net">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Net profit (collected − expenses)</div>
            <div className={`font-heading mt-2 text-4xl font-bold ${pl.net >= 0 ? "text-status-confirmed" : "text-destructive"}`}>{fmt(pl.net)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{MONTHS.find((m) => m.value === month)?.label}</div>
          </div>
        </div>
      )}
    </div>
  );
}
